import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { calculateTotalAvailable } from '../../utils/billingCalculations';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseButton,
  PaymentsTable,
  TableHeader,
  TableRow,
  TableCell,
  PaymentType,
  PaymentAmount,
  PaymentMethod,
  PaymentRate,
  PaymentAddedBy,
  PaymentTimestamp,
  TermsAndConditions,
  TermsStatus,
  TermsVersion,
  TermsUpdatedOn,
  TermsDetails,
  NoPayments,
  TableContainer,
  SortButton,
  NoBillingInfo,
  TotalAvailable,
  AddPaymentButton,
  DeleteButton
} from './ViewBillingModal.styled';
import { X, ArrowUpDown, PlusCircle, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import PaymentModal from '../PaymentModal/PaymentModal';

const ViewBillingModal = ({ show, onClose, administrator, onAddPayment, onDeletePayment }) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  const totalAvailable = useMemo(() => {
    return calculateTotalAvailable(administrator?.billing?.payments);
  }, [administrator?.billing?.payments]);

  const sortedPayments = useMemo(() => {
    if (!administrator?.billing?.payments) return [];
    
    return [...administrator.billing.payments].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (sortField === 'timestamp') {
        return sortDirection === 'asc' 
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      }
      
      return sortDirection === 'asc'
        ? (aValue || '').toString().localeCompare((bValue || '').toString())
        : (bValue || '').toString().localeCompare((aValue || '').toString());
    });
  }, [administrator?.billing?.payments, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddPayment = async (values) => {
    try {
      await onAddPayment(administrator._id.email, values);
      setShowPaymentModal(false);
    } catch (error) {
      // Handle error appropriately
    }
  };

  const handleDeleteClick = (payment) => {
    setPaymentToDelete(payment);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (paymentToDelete && onDeletePayment) {
      await onDeletePayment(paymentToDelete);
      setShowDeleteConfirmation(false);
      setPaymentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setPaymentToDelete(null);
  };

  if (!show) return null;

  const hasBillingInfo = administrator?.billing;
  const hasPayments = administrator?.billing?.payments?.length > 0;
  const hasTerms = administrator?.billing?.termsAndConditions;

  return (
    <>
      <Modal>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{t('billingInformation')}</ModalTitle>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>
          <ModalBody>
            {!hasBillingInfo ? (
              <NoBillingInfo>{t('noBillingInfo')}</NoBillingInfo>
            ) : (
              <>
                {hasTerms && (
                  <TermsAndConditions>
                    <TermsStatus accepted={administrator.billing.termsAndConditions.accepted}>
                      {administrator.billing.termsAndConditions.accepted 
                        ? t('termsAccepted') 
                        : t('termsNotAccepted')}
                    </TermsStatus>
                    <TermsDetails>
                      <TermsVersion>{t('version')}: {administrator.billing.termsAndConditions.version}</TermsVersion>
                      <TermsUpdatedOn>
                        {t('updatedOn')}: {format(new Date(administrator.billing.termsAndConditions.updatedOn), 'PPpp')}
                      </TermsUpdatedOn>
                    </TermsDetails>
                  </TermsAndConditions>
                )}

                <TotalAvailable>
                  {t('totalAvailable')}: {totalAvailable.toFixed(2)}€
                </TotalAvailable>

                <TableContainer>
                  <PaymentsTable>
                    <thead>
                      <tr>
                        <TableHeader>
                          <SortButton onClick={() => handleSort('type')}>
                            {t('type')} <ArrowUpDown size={14} />
                          </SortButton>
                        </TableHeader>
                        <TableHeader>
                          <SortButton onClick={() => handleSort('amount')}>
                            {t('amount')} <ArrowUpDown size={14} />
                          </SortButton>
                        </TableHeader>
                        <TableHeader>
                          <SortButton onClick={() => handleSort('rate')}>
                            {t('rate')} <ArrowUpDown size={14} />
                          </SortButton>
                        </TableHeader>
                        <TableHeader>
                          <SortButton onClick={() => handleSort('method')}>
                            {t('method')} <ArrowUpDown size={14} />
                          </SortButton>
                        </TableHeader>
                        <TableHeader>
                          <SortButton onClick={() => handleSort('addedBy')}>
                            {t('addedBy')} <ArrowUpDown size={14} />
                          </SortButton>
                        </TableHeader>
                        <TableHeader>
                          <SortButton onClick={() => handleSort('timestamp')}>
                            {t('date')} <ArrowUpDown size={14} />
                          </SortButton>
                        </TableHeader>
                        <TableHeader>
                          {t('actions')}
                        </TableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {!hasPayments ? (
                        <tr>
                          <TableCell colSpan={7}>
                            <NoPayments>{t('noPayments')}</NoPayments>
                          </TableCell>
                        </tr>
                      ) : (
                        sortedPayments.map((payment, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <PaymentType type={payment.type}>
                                {t(payment.type)}
                              </PaymentType>
                            </TableCell>
                            <TableCell>
                              <PaymentAmount type={payment.type}>
                                {payment.amount.toLocaleString()}
                              </PaymentAmount>
                            </TableCell>
                            <TableCell>
                              <PaymentRate>
                                {payment.rate ? `${payment.rate}%` : '-'}
                              </PaymentRate>
                            </TableCell>
                            <TableCell>
                              <PaymentMethod>
                                {payment.method || '-'}
                              </PaymentMethod>
                            </TableCell>
                            <TableCell>
                              <PaymentAddedBy>
                                {payment.addedBy}
                              </PaymentAddedBy>
                            </TableCell>
                            <TableCell>
                              <PaymentTimestamp>
                                {format(new Date(payment.timestamp), 'PPpp')}
                              </PaymentTimestamp>
                            </TableCell>
                            <TableCell>
                              <DeleteButton 
                                onClick={() => handleDeleteClick(payment)}
                                title={t('deletePayment')}
                              >
                                <Trash2 size={16} />
                              </DeleteButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </tbody>
                  </PaymentsTable>
                </TableContainer>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {hasBillingInfo && (
              <AddPaymentButton onClick={() => setShowPaymentModal(true)}>
                <PlusCircle size={16} />
                <span>{t('addPayment')}</span>
              </AddPaymentButton>
            )}
            <button onClick={onClose}>{t('close')}</button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {showPaymentModal && (
        <PaymentModal
          show={showPaymentModal}
          onHide={() => setShowPaymentModal(false)}
          onAddPayment={handleAddPayment}
          onDeletePayment={onDeletePayment}
          userBilling={administrator?.billing}
        />
      )}

      {showDeleteConfirmation && (
        <Modal>
          <ModalContent style={{ maxWidth: '400px' }}>
            <ModalHeader>
              <ModalTitle>{t('deletePayment')}</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <p>{t('confirmDeletePayment')}</p>
            </ModalBody>
            <ModalFooter>
              <button onClick={handleDeleteCancel}>{t('cancel')}</button>
              <DeleteButton onClick={handleDeleteConfirm}>
                {t('delete')}
              </DeleteButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ViewBillingModal; 